/**
 * Componente Header Inmersivo
 * Cabecera transparente que cambia según el scroll
 * Estilo tipo Heinz/Apple con transiciones suaves
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Logo from '../common/Logo';

gsap.registerPlugin(ScrollTrigger);

/**
 * Header inmersivo con cambio dinámico de estilos
 * - Transparente al inicio
 * - Fondo sólido al hacer scroll
 * - Menú hamburguesa animado
 * 
 * @returns {JSX.Element} Componente Header
 */
const ImmersiveHeader = () => {
  const headerRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const lastScrollY = useRef(0);
  const location = useLocation();

  // Enlaces de navegación
  const navLinks = [
    { name: 'Inicio', path: '/' },
    { name: 'Productos', path: '/productos' },
    { name: 'Recetas', path: '/recetas' },
    { name: 'Nosotros', path: '/nosotros' },
    { name: 'Noticias', path: '/noticias' },
    { name: 'Portal de Transparencia', path: '/transparencia' },
    { name: 'Contacto', path: '/contacto' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Cambiar estado de scroll
      setIsScrolled(currentScrollY > 50);
      
      // Ocultar/mostrar header al hacer scroll
      if (currentScrollY > lastScrollY.current && currentScrollY > 200) {
        setIsHidden(true);
      } else {
        setIsHidden(false);
      }
      
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cerrar menú al cambiar de ruta
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Animación del menú
  useEffect(() => {
    const menuItems = document.querySelectorAll('.nav-link-item');
    
    if (isMenuOpen) {
      gsap.fromTo(menuItems,
        { y: 50, opacity: 0 },
        { 
          y: 0, 
          opacity: 1, 
          duration: 0.5, 
          stagger: 0.1,
          ease: 'power3.out',
        }
      );
    }
  }, [isMenuOpen]);

  return (
    <>
      {/* Header principal */}
      <header
        ref={headerRef}
        className={`
          fixed top-0 left-0 right-0 z-50
          transition-all duration-500 ease-out
          ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}
          ${isHidden && !isMenuOpen ? '-translate-y-full' : 'translate-y-0'}
        `}
      >
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <Link 
              to="/" 
              className="relative z-50 transition-transform duration-300 hover:scale-105"
            >
              <Logo 
                className={`h-12 md:h-16 transition-all duration-300 ${
                  isScrolled || isMenuOpen ? '' : 'brightness-0 invert'
                }`}
              />
            </Link>

            {/* Navegación Desktop */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`
                    relative font-lato font-medium text-sm uppercase tracking-wider
                    transition-colors duration-300
                    ${isScrolled 
                      ? 'text-intercasa-dark hover:text-intercasa-red' 
                      : 'text-white hover:text-intercasa-yellow'
                    }
                    ${location.pathname === link.path ? 'font-bold' : ''}
                  `}
                >
                  {link.name}
                  {/* Línea activa */}
                  <span 
                    className={`
                      absolute -bottom-1 left-0 h-0.5 bg-intercasa-red
                      transition-all duration-300
                      ${location.pathname === link.path ? 'w-full' : 'w-0'}
                    `}
                  />
                </Link>
              ))}
            </nav>

            {/* Botón hamburguesa */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="relative z-50 w-10 h-10 flex flex-col items-center justify-center gap-1.5 lg:hidden"
              aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
            >
              <span 
                className={`
                  block w-6 h-0.5 transition-all duration-300
                  ${isScrolled || isMenuOpen ? 'bg-intercasa-dark' : 'bg-white'}
                  ${isMenuOpen ? 'rotate-45 translate-y-2' : ''}
                `}
              />
              <span 
                className={`
                  block w-6 h-0.5 transition-all duration-300
                  ${isScrolled || isMenuOpen ? 'bg-intercasa-dark' : 'bg-white'}
                  ${isMenuOpen ? 'opacity-0' : ''}
                `}
              />
              <span 
                className={`
                  block w-6 h-0.5 transition-all duration-300
                  ${isScrolled || isMenuOpen ? 'bg-intercasa-dark' : 'bg-white'}
                  ${isMenuOpen ? '-rotate-45 -translate-y-2' : ''}
                `}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Menú móvil fullscreen */}
      <div
        className={`
          fixed inset-0 z-40 bg-white
          transition-all duration-500 ease-out lg:hidden
          ${isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}
        `}
      >
        <div className="flex flex-col items-center justify-center h-full">
          <nav className="flex flex-col items-center gap-8">
            {navLinks.map((link, index) => (
              <Link
                key={link.path}
                to={link.path}
                className={`
                  nav-link-item
                  font-baskerville text-3xl md:text-4xl font-semibold
                  text-intercasa-dark hover:text-intercasa-red
                  transition-colors duration-300
                  ${location.pathname === link.path ? 'text-intercasa-red' : ''}
                `}
              >
                {link.name}
              </Link>
            ))}
          </nav>
          
          {/* Redes sociales en el menú */}
          <div className="mt-12 flex gap-6">
            <a 
              href="https://www.instagram.com/intercasacanarias/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-intercasa-dark hover:text-intercasa-red transition-colors"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a 
              href="https://www.linkedin.com/company/intercasa-s-a-" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-intercasa-dark hover:text-intercasa-red transition-colors"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default ImmersiveHeader;
